import { Injectable, Injector, inject } from '@angular/core';
import { Capacitor } from '@capacitor/core';
import {
  ActivatedRouteSnapshot,
  CanActivate,
  Router,
  RouterStateSnapshot,
} from '@angular/router';
import { AuthService } from '../auth/auth.service';
import { CompanySettingsService } from '../../../settings/services/company-settings.service';
import { UserPermissionsService } from '../../../employee/services/user-permissions.service';
import { SubscriptionService } from '../../../feature-toggle/services/subscription.service';
import { FirebaseLazyLoaderService } from '../../../../core/services/firebase-lazy-loader.service';
import { MatomoLazyLoaderService } from '../../../../core/services/matomo-lazy-loader.service';
import { firstValueFrom } from 'rxjs';
import { ClientLoggerService } from 'src/app/core/services/logging/client-logger.service';
import { LogCategory } from 'src/app/core/services/logging/client-log.models';
import { isTerminalSession } from 'src/app/core/services/session/terminal-session';

@Injectable({
  providedIn: 'root',
})
export class AuthGuard implements CanActivate {
  private settingsLoaded = false;
  private readonly injector = inject(Injector);
  private loadingPromise: Promise<void> | null = null;

  constructor(
    private router: Router,
    private service: AuthService,
    private companySettingsService: CompanySettingsService,
    private userPermissionsService: UserPermissionsService,
    private subscriptionService: SubscriptionService,
    private firebaseLoader: FirebaseLazyLoaderService,
    private matomoLoader: MatomoLazyLoaderService,
    private logger: ClientLoggerService
  ) {
    // Reset settingsLoaded flag when user logs out
    this.service.isLoggedOut$.subscribe((isLoggedOut) => {
      if (isLoggedOut) {
        this.settingsLoaded = false;
        this.loadingPromise = null;
      }
    });
  }

  async canActivate(
    next: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Promise<boolean> {
    const token = localStorage.getItem('token');
    if (token != null) {
      // Load settings/permissions/features on first authenticated navigation
      // Use shared promise to prevent multiple parallel loads
      if (!this.settingsLoaded) {
        if (!this.loadingPromise) {
          this.loadingPromise = this.loadUserSettings()
            .then(() => {
              this.settingsLoaded = true;
            })
            .catch((error) => {
              console.error('Failed to load user settings in AuthGuard:', error);
              // Reset to allow retry on next navigation
              this.loadingPromise = null;
              // Continue anyway - don't block navigation
            });
        }

        try {
          await this.loadingPromise;
          await this.loadLazyModules();
        } catch (error) {
          // Error already logged above
        }
      }

      const roles = next.data['permittedRoles'] as Array<string>;
      if (roles) {
        if (this.service.roleMatch(roles)) {
          return true;
        } else {
          this.router.navigate(['/forbidden']);
          return false;
        }
      }
      return true;
    } else {
      // C-08: Der Rückweg wandert mit. Vorher landete man auf `/login` und nach der
      // Anmeldung auf dem Dashboard — die Seite, die man aufgerufen hatte, war weg.
      // `reason` setzt der Guard NICHT: Er sieht nur „kein Token", und das kann auch
      // heißen „noch nie angemeldet". Wer wegen Ablauf hier landet, kommt über
      // `AuthService.goToLogin('expired')` und bringt den Grund mit.
      this.router.navigate(['/login'], {
        queryParams: state.url && state.url !== '/' ? { returnUrl: state.url } : {},
      });
      return false;
    }
  }

  private async loadUserSettings(): Promise<void> {
    // Check if settings are already cached to avoid redundant API calls
    const cachedSettings = localStorage.getItem('CompanySettings');
    const cachedPermissions = sessionStorage.getItem('currentUserPermissions');

    // Verbesserte Cache-Prüfung: Bei Terminal-Rolle reichen cached Settings
    const isTerminal = this.service.isTerminal;

    if (cachedSettings && cachedPermissions) {
      return;
    }

    // Falls Settings gecached sind aber Permissions fehlen (z.B. nach sessionStorage clear)
    // Für Terminal reichen die Settings, Permissions können optional nachgeladen werden
    if (cachedSettings && isTerminal) {
      // Versuche Permissions asynchron nachzuladen ohne zu warten
      this.userPermissionsService.initGetCurrentUserPermissions().catch(err =>
        console.warn('AuthGuard: Optional permissions load failed for terminal:', err)
      );
      return;
    }

    try {
      await Promise.all([
        this.companySettingsService.initGetCompanySettings(),
        this.userPermissionsService.initGetCurrentUserPermissions(),
        firstValueFrom(this.subscriptionService.loadCompanyFeatures()),
      ]);
    } catch (error) {
      this.logger.logError(error, {
        componentName: 'AuthGuard.loadUserSettings',
        category: LogCategory.System,
        properties: {
          operation: 'guard-load-settings',
          hasToken: !!localStorage.getItem('token')
        }
      });
      console.error('AuthGuard: Error loading user settings', error);
      throw error;
    }
  }

  /**
   * Reset the guard state - should be called on logout
   */
  public resetState(): void {
    this.settingsLoaded = false;
    this.loadingPromise = null;
  }

  private async loadLazyModules(): Promise<void> {
    // Load Firebase and Matomo in parallel, non-blocking
    Promise.all([
      this.firebaseLoader.initializeFirebase().then(() => this.registerWebPush()).catch(err => {
        this.logger.logWarning('Firebase initialization failed', LogCategory.System, {
          error: err.message,
          operation: 'firebase-init'
        });
        console.warn('Firebase initialization failed (non-critical):', err);
      }),
      this.matomoLoader.initializeMatomo().catch(err => {
        this.logger.logWarning('Matomo initialization failed', LogCategory.System, {
          error: err.message,
          operation: 'matomo-init'
        });
        console.warn('Matomo initialization failed (non-critical):', err);
      })
    ]);
  }

  /**
   * Web-Push-Token erst NACH der Firebase-Initialisierung anmelden (E2E 17.10.2026). Vorher rief nur
   * `AppComponent` beim Start `requestPermission()` — zu früh: Firebase lädt erst hier, der Aufruf
   * endete mit „not yet initialized“, und im Browser wurde nie ein Token registriert (lokal kein
   * einziger Web-Eintrag in `ClientDevice`). Nativ registriert `FcmService`; das Terminal gehört
   * keiner Person und bekommt keine persönlichen Meldungen.
   */
  private registerWebPush(): void {
    if (Capacitor.getPlatform() !== 'web' || isTerminalSession()) return;
    void import('../../../../core/services/notification/push-notifications.service').then(({ PushNotificationsService }) =>
      this.injector.get(PushNotificationsService).requestPermission(),
    );
  }
}
